import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Sidebar as SidebarShell, SidebarProvider } from "@/components/_atoms/sidebar";
import { cn } from "@/lib/cn";
import { useLeftSidebarStore } from "@/stores/left-aside-store";

import { useLayout } from "../use-layout";

const MIN_WIDTH = 220;
const MAX_WIDTH = 360;
const DEFAULT_WIDTH = 248;
const RESIZE_STEP = 10;

export const Sidebar: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation("common");
  const { isHidden, setIsHidden } = useLeftSidebarStore();
  const { offsetTop } = useLayout();

  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const shellRef = useRef<HTMLDivElement>(null);

  const startResizing = useCallback(() => {
    if (isHidden) {
      return;
    }
    setIsResizing(true);
  }, [isHidden]);

  const stopResizing = useCallback(() => setIsResizing(false), []);

  const handleResizeKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isHidden) {
        return;
      }
      if (event.key === "ArrowLeft") {
        setWidth((current) => Math.max(MIN_WIDTH, current - RESIZE_STEP));
      }
      if (event.key === "ArrowRight") {
        setWidth((current) => Math.min(MAX_WIDTH, current + RESIZE_STEP));
      }
    },
    [isHidden],
  );

  const resize = useCallback((event: MouseEvent) => {
    if (!shellRef.current) {
      return;
    }
    const nextWidth = event.clientX - shellRef.current.getBoundingClientRect().left;
    setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, nextWidth)));
  }, []);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  return (
    <SidebarProvider
      open={!isHidden}
      onOpenChange={(open) => setIsHidden(!open)}
      className={cn("min-h-0 w-auto", isResizing && "[&_*]:transition-none!")}
      style={{ "--sidebar-width": `${width}px` } as React.CSSProperties}
    >
      <SidebarShell ref={shellRef} side="left" collapsible="offcanvas" aria-label={t("sidebar.sidebar")}>
        <nav
          aria-label={t("sidebar.navigation")}
          className="relative flex h-full flex-col bg-canvas"
          style={{ paddingTop: `${offsetTop}px` }}
        >
          {children}
        </nav>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t("sidebar.resizeSidebar")}
          aria-valuenow={width}
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          tabIndex={0}
          onMouseDown={startResizing}
          onKeyDown={handleResizeKeyDown}
          className={cn(
            "group absolute inset-y-0 z-20 flex cursor-col-resize items-center justify-center",
            isResizing ? "-right-8 w-16" : "-right-1.5 w-3",
          )}
        >
          <div
            className={cn(
              "h-full w-0.5 rounded-full bg-transparent group-hover:bg-muted-foreground",
              isResizing && "bg-muted-foreground",
            )}
          />
        </div>
      </SidebarShell>
    </SidebarProvider>
  );
};
